import { Panel, ProgressBar } from '@elotech/components';
import React, { createContext, useCallback, useContext } from 'react';

import { AsyncTask } from '../../type';
import { InlineButton } from '../inline-button';

type ContextValue = { task: AsyncTask };

const AsyncTaskProgressContext = createContext<ContextValue>({
  task: {} as AsyncTask
});

const useAsyncTaskProgress = () => useContext(AsyncTaskProgressContext);

type RootProps = {
  task?: AsyncTask | null;
  children: React.ReactNode;
};

const Root = ({ task, children }: RootProps) => {
  if (!task || task?.finished) return null;
  return (
    <AsyncTaskProgressContext.Provider value={{ task }}>
      <Panel className="mt-xs mb-xs">{children}</Panel>
    </AsyncTaskProgressContext.Provider>
  );
};

const Status = () => {
  const { task } = useAsyncTaskProgress();

  const canceledMessage = task?.cancelled ? 'Cancelado' : undefined;
  const errorMessage = task?.errorMessage ? 'Erro' : undefined;

  const statusMessage = canceledMessage || errorMessage || task.currentStatus;

  return <div className="panel-title">{statusMessage}</div>;
};

const Bar = () => {
  const { task } = useAsyncTaskProgress();

  const calcProgressPercent = useCallback(() => {
    if (!task?.total || !task.current) return 0;
    return Math.round((100 * task.current) / task.total);
  }, [task]);

  return <ProgressBar percentualProgresso={calcProgressPercent()} />;
};

const ErrorMessage = ({ fallback }: { fallback?: string }) => {
  const { task } = useAsyncTaskProgress();
  const error = task?.errorMessage || fallback;

  if (!error || task?.running) {
    return null;
  }

  return (
    <div className="async-task-progress-error" style={{ color: '#d32f2f' }}>
      {error}
    </div>
  );
};

const Actions = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
    {children}
  </div>
);

type ActionButtonProps = {
  onClick: () => void;
  label?: string;
  disabled?: boolean;
};

const CancelButton = ({
  onClick,
  label = 'Cancelar',
  disabled
}: ActionButtonProps) => {
  const { task } = useAsyncTaskProgress();

  if (!task?.running || !task.cancellable) {
    return null;
  }

  return (
    <InlineButton
      color="negative"
      icon="fa fa-times"
      label={label}
      onClick={onClick}
      disabled={disabled}
    />
  );
};

const RetryButton = ({
  onClick,
  label = 'Tentar Novamente',
  disabled
}: ActionButtonProps) => {
  const { task } = useAsyncTaskProgress();

  if (task?.running) {
    return null;
  }

  return (
    <InlineButton
      color="module-color"
      icon="fa fa-redo"
      label={label}
      onClick={onClick}
      disabled={disabled}
    />
  );
};

export const AsyncTaskProgress = Object.assign(Root, {
  Status,
  Bar,
  Error: ErrorMessage,
  Actions,
  CancelButton,
  RetryButton
});
